import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { ChevronRight, Ellipsis, Trash2 } from 'lucide-react';
import type { Instruction } from '@/types';
import { Separator } from '@/components/ui/separator';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { DeleteDialog } from '@/components/shared/delete-dialog';
import { useKnowledgeBases } from '@/hooks';
import { useToggleInstructionStatus, useDeleteInstruction } from '@/hooks';
import { toast } from '@/components/ui/sonner';
import { useState } from 'react';

interface InstructionCardProps {
  instruction: Instruction;
  onClick?: (instruction: Instruction) => void;
}

export function InstructionCard({ instruction, onClick }: InstructionCardProps) {
  const { data: knowledgeBases } = useKnowledgeBases();
  const toggleStatusMutation = useToggleInstructionStatus();
  const deleteInstructionMutation = useDeleteInstruction();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const handleClick = () => {
    onClick?.(instruction);
  };

  const handleDelete = async () => {
    try {
      await deleteInstructionMutation.mutateAsync(instruction.id);
      toast.success('Instruction deleted successfully');
      setDeleteDialogOpen(false);
    } catch {
      toast.error('Failed to delete instruction');
    }
  };

  const handleToggleStatus = async (checked: boolean) => {
    try {
      await toggleStatusMutation.mutateAsync({
        id: instruction.id,
        status: checked
      });
      toast.success(`Instruction ${checked ? 'enabled' : 'disabled'} successfully`);
    } catch {
      toast.error('Failed to update instruction status');
    }
  };

  const getKnowledgeBaseName = (kbId: string): string => {
    const kb = knowledgeBases?.find((kb) => kb.id === kbId);
    return kb?.name || 'Unknown Knowledge Base';
  };

  return (
    <Card className='rounded-2xl cursor-pointer hover:drop-shadow-lg hover:drop-shadow-black/5 hover:bg-gray-50 transition-shadow h-[190px] flex flex-col' onClick={handleClick}>
      <CardContent className=' flex flex-col h-full'>
        {/* First Row: Name and Status */}
        <div className='flex justify-between items-center gap-2 mb-2 -mt-2'>
          <div className='flex-1 max-w-[60%]'>
            <h3 className='font-medium text-sm line-clamp-1 gap-2 flex items-center'>
              {instruction.name}{' '}
              {instruction.type === 'escalation' && (
                <Badge variant='secondary' className='text-xs px-2 py-1 h-5 font-normal bg-gray-200'>
                  Escalate
                </Badge>
              )}
            </h3>
          </div>
          <div className='flex items-center gap-1.5'>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button className='cursor-pointer bg-inherit border-none shadow-none' variant='outline' size='icon' onClick={(e) => e.stopPropagation()}>
                  <Ellipsis className='h-3 w-3' />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align='end' onClick={(e) => e.stopPropagation()}>
                <DropdownMenuItem variant='default' onSelect={() => setDeleteDialogOpen(true)}>
                  <Trash2 className='h-4 w-4 mr-2' />
                  Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <Separator orientation='vertical' className='h-4! bg-gray-400 mr-1.5' />
            <Switch
              className='cursor-pointer'
              checked={instruction.status}
              onCheckedChange={handleToggleStatus}
              disabled={toggleStatusMutation.isPending}
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </div>
        
        <div className='border-b' />

        {/* Second Row: Description */}
        <p className='text-xs h-16 line-clamp-4 mt-3 mb-5 text-ink-strong'>{instruction.description}</p>

        {/* Environments Row */}
        {/* {shadowModeEnabled && instruction.tags && instruction.tags.length > 0 && (
          <div className='flex gap-1 flex-wrap mb-2'>
            {instruction.tags.slice(0, 3).map((tag, index) => (
              <Badge key={index} variant='outline' className='text-[9px] px-1.5 py-0 h-4 font-normal'>
                {tag}
              </Badge>
            ))}
            {instruction.tags.length > 3 && (
              <Badge variant='outline' className='text-[9px] px-1.5 py-0 h-4 font-normal'>
                +{instruction.tags.length - 3}
              </Badge>
            )}
          </div>
        )} */}

        {/* Last Row: Knowledge Base Tags */}
        <div className='flex gap-1 flex-wrap'>
          {instruction.knowledgeBases && instruction.knowledgeBases.length > 0 && (
            <>
              <Badge variant='secondary' className='text-xs px-3.5 py-1.5 font-medium text-gray-900'>
                {getKnowledgeBaseName(instruction.knowledgeBases[0])}
              </Badge>
              {instruction.knowledgeBases.length > 1 && (
                <Badge variant='secondary' className='text-xs px-3.5 py-1.5 font-medium text-gray-900'>
                  +{instruction.knowledgeBases.length - 1}
                </Badge>
              )}
            </>
          )}
        </div>

        <ChevronRight className='absolute right-4 bottom-5 h-5 w-5 text-muted-foreground' />
      </CardContent>

      <DeleteDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        onConfirm={handleDelete}
        title='Delete Instruction'
        description='Are you sure you want to delete this instruction? This action cannot be undone.'
        itemName={instruction.name}
        isLoading={deleteInstructionMutation.isPending}
      />
    </Card>
  );
}
